/**
 * AZ ÁTADÓ KÓD BEVÁLTÁSA (#76) — az admin új lapja `#maszk=<kód>`-dal nyit.
 *
 * A kód a FRAGMENTBEN jön, a varázslink mintájára (`App.tsx`,
 * `readMagicLinkToken`): a böngésző nem küldi el a szervernek, így nem kerül
 * sem a statikus kiszolgáló naplójába, sem a `Referer`-be. Beolvasás után
 * AZONNAL kikerül a címsorból — egy könyvjelző vagy egy továbbküldött cím ne
 * hordozza (a kód amúgy is egyszer használható és egy percig él).
 *
 * ⚠️ EGYSZER VÁLTJA BE, `StrictMode`-ban is. A fejlesztői mód kétszer futtatja
 * az effektet; a második beváltás 401-et kapna (a kód elhasználódott), és a
 * lap a „nem érvényes" képernyőre ugrana egy sikeres belépés után.
 */

import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/AuthProvider';
import { ApiError } from '@/lib/api-client';

export type HandoffState =
  | { kind: 'none' }
  | { kind: 'pending' }
  | { kind: 'failed'; message: string | null };

export function readImpersonationCode(hash: string = window.location.hash): string | null {
  const match = /(?:^|&)maszk=([A-Za-z0-9_-]+)/.exec(hash.replace(/^#/, ''));
  return match === null ? null : (match[1] ?? null);
}

export function useImpersonationHandoff(): HandoffState {
  const { redeemImpersonation } = useAuth();
  const [code] = useState(readImpersonationCode);
  const [state, setState] = useState<HandoffState>(
    code === null ? { kind: 'none' } : { kind: 'pending' }
  );
  const started = useRef(false);

  useEffect(() => {
    if (code === null || started.current) {
      return;
    }
    started.current = true;
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    redeemImpersonation(code)
      .then(() => setState({ kind: 'none' }))
      .catch((caught: unknown) =>
        setState({
          kind: 'failed',
          message: caught instanceof ApiError && caught.message ? caught.message : null,
        })
      );
  }, [code, redeemImpersonation]);

  return state;
}
